import { createRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Route as AdminLayoutRoute } from "./_layout";
import { Button } from "@/components/base/buttons/button";
import { StatCard } from "@/components/application/metrics/stat-card";
import { SyncHistoryTable, type SyncHistoryRow } from "../../components/custom/SyncHistoryTable";
import { SyncRunDetailSlideOver } from "../../components/admin/SyncRunDetailSlideOver";
import { useSyncProgressSse, type SyncStateZoneSnapshot, type SyncProgressEvent } from "../../hooks/useSyncProgressSse";
import { apiCall, UnauthorizedError } from "../../lib/apiClient";

/**
 * UC-001 Sync admin UI — per-Středisko sync.
 *
 * Výběr středisek klikem na kartu (toggle); jedno tlačítko "Synchronizovat"
 * spustí vybraná střediska SEKVENČNĚ (backend Playwright není thread-safe).
 *   - Karty S0/S1/S2 = přepínač výběru (aria-pressed).
 *   - "Vybrat vše / Zrušit výběr" hromadný přepínač.
 *   - Během běhu: běžící zóna "Synchronizuji…", čekající "Ve frontě…".
 *   - Po dokončení: souhrn (které zóny OK / selhaly).
 *   - SyncHistoryTable (řazení dle zahájení DESC + filtr stavů a středisek).
 */
export const Route = createRoute({
  getParentRoute: () => AdminLayoutRoute,
  path: "/admin/sync",
  component: AdminSyncPage,
});

interface ZoneSyncResult {
  zone_code: string;
  ok: boolean;
}

function AdminSyncPage() {
  const { snapshot, zones, refreshState } = useSyncProgressSse();
  const [history, setHistory] = useState<SyncHistoryRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  // Vybraná střediska (zone_code). Default = prázdné (nic předvybráno) — uživatel
  // klikem na karty vybere, která střediska synchronizovat.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // zone_code právě běžící zóny (per-card loading), nebo null; + globální flag.
  const [runningZone, setRunningZone] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [triggerError, setTriggerError] = useState<string | null>(null);
  // Souhrn posledního běhu (C) — null dokud neproběhl žádný sync v této session.
  const [summary, setSummary] = useState<ZoneSyncResult[] | null>(null);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  async function fetchHistory(): Promise<void> {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const resp = await apiCall<{ rows: SyncHistoryRow[] }>(
        "/api/admin/sync/history?limit=50",
      );
      setHistory(resp.rows ?? []);
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        // Globální handler v apiClient zařídí redirect na /login; nemá smysl
        // renderovat error banner, který user uvidí jen na zlomek vteřiny.
        return;
      }
      setHistoryError(err instanceof Error ? err.message : "history_error");
    } finally {
      setHistoryLoading(false);
    }
  }

  useEffect(() => {
    void fetchHistory();
  }, []);

  // Po dokončení sync (jakákoli zóna → terminal phase) refresh historie + state.
  const liveDones = Object.values(snapshot.byZone).filter(
    (e) => e.phase === "done",
  ).length;
  useEffect(() => {
    if (liveDones > 0) {
      void fetchHistory();
      void refreshState();
    }
  }, [liveDones, refreshState]);

  const visibleZones = ensureThreeZones(zones, snapshot.byZone);
  const isPlaceholder = (z: SyncStateZoneSnapshot): boolean =>
    z.zone_id.startsWith("placeholder-");

  // Střediska, která lze vybrat (existují reálně, ne placeholder).
  const selectableCodes = useMemo(
    () => visibleZones.filter((z) => !isPlaceholder(z)).map((z) => z.zone_code),
    [visibleZones],
  );
  const allSelected =
    selectableCodes.length > 0 && selectableCodes.every((c) => selected.has(c));

  function toggleZone(code: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  // A — hromadný výběr/odznačení všech vybratelných středisek.
  function toggleAll(): void {
    setSelected(allSelected ? new Set() : new Set(selectableCodes));
  }

  // Synchronizuj vybraná střediska SEKVENČNĚ. Backend Playwright NENÍ thread-safe
  // (SyncOrchestrator.runAllZones jede zóny sériově ze stejného důvodu), takže
  // paralelní per-zóna POSTy by kolidovaly — await jeden po druhém.
  async function triggerSync(): Promise<void> {
    const targets = visibleZones.filter(
      (z) => selected.has(z.zone_code) && !isPlaceholder(z),
    );
    if (targets.length === 0) return;
    setSyncing(true);
    setTriggerError(null);
    setSummary(null); // C — nový běh čistí předchozí souhrn i chybu
    const results: ZoneSyncResult[] = [];
    try {
      for (const zone of targets) {
        setRunningZone(zone.zone_code);
        try {
          await apiCall<unknown>(`/api/admin/sync/${zone.zone_id}`, { method: "POST" });
          results.push({ zone_code: zone.zone_code, ok: true });
          // Po dokončení syncu zóny ji odznač — uživatel vidí, co už proběhlo.
          setSelected((prev) => {
            const next = new Set(prev);
            next.delete(zone.zone_code);
            return next;
          });
        } catch (err) {
          if (err instanceof UnauthorizedError) return;
          results.push({ zone_code: zone.zone_code, ok: false });
          setTriggerError(err instanceof Error ? err.message : "sync_trigger_failed");
          // při chybě zónu necháme vybranou (pro případný retry)
        }
      }
    } finally {
      setRunningZone(null);
      setSyncing(false);
      setSummary(results);
      void fetchHistory();
      void refreshState();
    }
  }

  // D — text pro aria-live oznámení průběhu (čtečky + vizuálně).
  const liveStatus = syncing
    ? runningZone
      ? `Synchronizuji středisko ${runningZone}…`
      : "Synchronizace probíhá…"
    : summary
      ? `Synchronizace dokončena: ${summary.filter((r) => r.ok).length} OK${
          summary.some((r) => !r.ok)
            ? `, ${summary.filter((r) => !r.ok).length} selhalo`
            : ""
        }.`
      : "";

  return (
    <section aria-labelledby="sync-title" className="max-w-7xl mx-auto space-y-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex flex-col gap-1">
          <h2 id="sync-title" className="text-2xl font-bold text-primary m-0">
            Synchronizace z Axigonu
          </h2>
          <p className="text-base text-tertiary m-0">
            Klikni na střediska (S0 / S1 / S2), která chceš synchronizovat —
            klik vybere, další klik odznačí — a spusť synchronizaci.
          </p>
          {selected.size === 0 && !syncing && (
            <p className="text-sm text-tertiary m-0">
              Vyberte alespoň jedno středisko.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            color="secondary"
            onPress={toggleAll}
            isDisabled={syncing || selectableCodes.length === 0}
            data-testid="sync-select-all"
          >
            {allSelected ? "Zrušit výběr" : "Vybrat vše"}
          </Button>
          <Button
            type="button"
            color="primary"
            onPress={() => void triggerSync()}
            isDisabled={syncing || selected.size === 0}
            isLoading={syncing}
            aria-label={
              selected.size === 0
                ? "Synchronizovat — nejdříve vyberte středisko"
                : undefined
            }
            data-testid="sync-trigger"
          >
            {syncing
              ? "Spouštím…"
              : `Synchronizovat${selected.size > 0 ? ` (${selected.size})` : ""}`}
          </Button>
        </div>
      </header>

      {/* D — live oznámení průběhu (vizuální i pro čtečky). */}
      <p
        role="status"
        aria-live="polite"
        className="text-sm text-tertiary m-0 min-h-[1.25rem]"
        data-testid="sync-live-status"
      >
        {liveStatus}
      </p>

      {triggerError && (
        <div className="text-sm text-error-primary" role="alert">
          Chyba při spouštění synchronizace: {triggerError}
        </div>
      )}

      {/* C — souhrn posledního běhu. */}
      {summary && summary.length > 0 && !syncing && (
        <div
          className="text-sm text-primary flex flex-wrap gap-x-3 gap-y-1"
          data-testid="sync-summary"
        >
          <span className="font-medium">Výsledek:</span>
          {summary.map((r) => (
            <span
              key={r.zone_code}
              className={r.ok ? "text-success-primary" : "text-error-primary"}
            >
              <span aria-hidden="true">{r.ok ? "✓" : "✗"}</span> {r.zone_code}
            </span>
          ))}
        </div>
      )}

      <div
        className="grid grid-cols-1 md:grid-cols-3 gap-4"
        data-testid="sync-zone-cards"
      >
        {visibleZones.map((zone) => (
          <ZoneStatCard
            key={zone.zone_code}
            zone={zone}
            live={snapshot.byZone[zone.zone_code]}
            selected={selected.has(zone.zone_code)}
            selectable={!isPlaceholder(zone) && !syncing}
            running={runningZone === zone.zone_code}
            queued={
              syncing &&
              runningZone !== zone.zone_code &&
              selected.has(zone.zone_code)
            }
            onToggle={() => toggleZone(zone.zone_code)}
          />
        ))}
      </div>

      <section aria-labelledby="sync-history-title" className="space-y-3">
        <h3 id="sync-history-title" className="text-lg font-semibold text-primary m-0">
          Historie běhů
        </h3>
        {historyError && (
          <p className="text-sm text-error-primary m-0" role="alert">
            Načítání historie selhalo: {historyError}
          </p>
        )}
        <SyncHistoryTable
          rows={history}
          loading={historyLoading}
          onRowClick={(r) => setSelectedRunId(r.sync_run_id)}
        />
      </section>
      <SyncRunDetailSlideOver
        runId={selectedRunId}
        onClose={() => setSelectedRunId(null)}
      />
    </section>
  );
}

function ensureThreeZones(
  zones: SyncStateZoneSnapshot[],
  liveByZone: Record<string, SyncProgressEvent>,
): SyncStateZoneSnapshot[] {
  const known = new Map<string, SyncStateZoneSnapshot>();
  zones.forEach((z) => known.set(z.zone_code, z));
  Object.values(liveByZone).forEach((e) => {
    if (!known.has(e.zone_code)) {
      known.set(e.zone_code, {
        zone_id: e.zone_id,
        zone_code: e.zone_code,
        billing_group_code: "—",
        active: true,
        last_state: e.state,
        last_sync_at: e.timestamp,
        duration_seconds: null,
        inventory_count: e.inventory_count,
        last_sync_run_id: e.sync_run_id,
        transaction_count: 0,
        card_count: 0,
      });
    }
  });
  ["S0", "S1", "S2"].forEach((code) => {
    if (!known.has(code)) {
      known.set(code, {
        zone_id: `placeholder-${code}`,
        zone_code: code,
        billing_group_code: "—",
        active: false,
        last_state: "never",
        last_sync_at: null,
        duration_seconds: null,
        inventory_count: null,
        last_sync_run_id: null,
        transaction_count: 0,
        card_count: 0,
      });
    }
  });
  return Array.from(known.values()).sort((a, b) =>
    a.zone_code.localeCompare(b.zone_code),
  );
}

function formatDuration(seconds: number | null): string {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds} s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

function formatDate(iso: string | null): string {
  if (iso == null) return "—";
  try {
    return new Date(iso).toLocaleString("cs-CZ");
  } catch {
    return iso;
  }
}

function stateLabel(state: string): string {
  switch (state) {
    case "kompletni":
      return "OK";
    case "castecny":
      return "Částečný";
    case "selhal":
      return "Selhal";
    case "running":
      return "Probíhá";
    default:
      return "Bez sync";
  }
}

function progressBarVariant(state: string): "info" | "success" | "warning" | "error" {
  switch (state) {
    case "kompletni":
      return "success";
    case "castecny":
      return "warning";
    case "selhal":
      return "error";
    default:
      return "info";
  }
}

// UUI utility překlad z legacy .zone-stat__state--* (text barva dle stavu).
const STATE_TEXT_CLASS: Record<"info" | "success" | "warning" | "error", string> = {
  success: "text-success-primary",
  warning: "text-warning-primary",
  error: "text-error-primary",
  info: "text-brand-secondary",
};

// UUI utility překlad z legacy .zone-stat__progress-bar--* (výplň progress baru).
const PROGRESS_BAR_CLASS: Record<"info" | "success" | "warning" | "error", string> = {
  success: "bg-success-solid",
  warning: "bg-warning-solid",
  error: "bg-error-solid",
  info: "bg-brand-solid",
};

interface ZoneStatCardProps {
  zone: SyncStateZoneSnapshot;
  live?: SyncProgressEvent;
  /** Karta je vybraná k synchronizaci. */
  selected?: boolean;
  /** Lze přepínat výběr (ne placeholder zóna, neběží sync). */
  selectable?: boolean;
  /** Tato zóna se právě synchronizuje. */
  running?: boolean;
  /** Vybraná zóna čekající ve frontě (běží jiná). */
  queued?: boolean;
  onToggle?: () => void;
}

function ZoneStatCard({
  zone,
  live,
  selected,
  selectable,
  running,
  queued,
  onToggle,
}: ZoneStatCardProps) {
  const displayedState = live?.state ?? zone.last_state;
  const documentCount = zone.inventory_count ?? 0;
  const transactionCount = zone.transaction_count ?? 0;
  const cardCount = zone.card_count ?? 0;
  const lastSyncIso = live?.timestamp ?? zone.last_sync_at;
  const duration = zone.duration_seconds;
  const progressPct = live ? live.progress_pct : null;
  const variant = progressBarVariant(displayedState);

  return (
    <div
      role="button"
      aria-pressed={selected ?? false}
      aria-disabled={!selectable}
      tabIndex={selectable ? 0 : -1}
      onClick={selectable ? onToggle : undefined}
      onKeyDown={
        selectable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onToggle?.();
              }
            }
          : undefined
      }
      className={`flex min-h-[120px] w-full flex-col gap-3 rounded-lg border border-secondary bg-primary p-4 text-left transition-shadow ${
        selected ? "ring-2 ring-brand bg-brand-solid/5" : "ring-1 ring-transparent"
      } ${selectable ? "cursor-pointer" : "cursor-default opacity-95"}`}
      data-testid={`zone-stat-${zone.zone_code}`}
      data-selected={selected ? "true" : "false"}
      data-zone-state={displayedState}
    >
      <div className="flex items-center justify-between mb-1">
        <span
          aria-hidden="true"
          className={`inline-flex h-5 w-5 items-center justify-center rounded border text-xs font-bold ${
            selected
              ? "bg-brand-solid text-white border-brand"
              : "border-secondary text-transparent"
          }`}
        >
          ✓
        </span>
        {running && (
          <span className="text-xs text-brand-secondary font-medium" data-testid={`zone-status-running-${zone.zone_code}`}>
            Synchronizuji…
          </span>
        )}
        {queued && !running && (
          <span className="text-xs text-tertiary font-medium" data-testid={`zone-status-queued-${zone.zone_code}`}>
            Ve frontě…
          </span>
        )}
      </div>
      <StatCard
        label={`Zóna ${zone.zone_code}`}
        value={
          <span className={`text-xl font-bold ${STATE_TEXT_CLASS[variant]}`}>
            {stateLabel(displayedState)}
          </span>
        }
        helper={
          <span>
            {`Poslední sync: ${formatDate(lastSyncIso)} · Doba: ${formatDuration(duration)}`}
            <br />
            {`Doklady: ${documentCount} · Transakce: ${transactionCount} · Karty: ${cardCount}`}
          </span>
        }
      />
      {progressPct != null && (
        <div
          className="relative h-6 overflow-hidden rounded-md bg-tertiary"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progressPct}
          aria-label={`Průběh synchronizace zóny ${zone.zone_code}`}
          data-testid={`zone-progress-${zone.zone_code}`}
        >
          <div
            className={`absolute inset-y-0 left-0 transition-[width] duration-200 ease-out ${PROGRESS_BAR_CLASS[variant]}`}
            style={{ width: `${progressPct}%` }}
          />
          <span className="relative inline-flex h-full items-center px-2 text-sm font-semibold text-primary mix-blend-multiply">
            {progressPct}% — fáze: {live?.phase ?? "—"}
          </span>
        </div>
      )}
    </div>
  );
}
